import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { methodologyService, Methodology, GuidedMethodology } from "../../lib/firebase";
import { useSystemSettings } from "../../lib/SystemSettingsContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import { toast } from "../../components/ui/use-toast";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { contributionService, Contribution, Contributor } from "../../lib/firebase";
import { TwoFactorAuthStorage } from "../../lib/twoFactorAuth";
import { neuralNetworkService } from "../../lib/firebase";

const ADMIN_EMAIL = "admin@rawpa.com";

export const AdminDashboard = (): JSX.Element => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userCount, setUserCount] = useState<number>(0);
  const [activeSessionCount, setActiveSessionCount] = useState<number>(0);
  const [feedbackCount, setFeedbackCount] = useState<number>(0);
  const [averageRating, setAverageRating] = useState<number>(0);
  const [helpfulPercentage, setHelpfulPercentage] = useState<number>(0);
  const auth = getAuth();
  const { settings, refreshSettings } = useSystemSettings();
  const [featuredMethodology, setFeaturedMethodology] = useState(settings.featuredMethodology);
  const [featuredToolkit, setFeaturedToolkit] = useState(settings.featuredToolkit);
  const [methodologyOptions, setMethodologyOptions] = useState<Array<{
    id: string;
    title: string;
    type: 'straight-up' | 'guided' | 'pathway';
    description?: string;
  }>>([]);
  const [toolkitOptions, setToolkitOptions] = useState<Array<{
    id: string;
    title: string;
    type: 'lolbas' | 'gtfobins' | 'wadcoms' | 'reverse-shell' | 'osint' | 'hunting-toolkit';
    description?: string;
  }>>([
    { id: 'lolbas', title: 'LOLBAS', type: 'lolbas', description: 'Living Off The Land Binaries And Scripts' },
    { id: 'gtfobins', title: 'GTFOBins', type: 'gtfobins', description: 'Unix binaries that can be exploited' },
    { id: 'wadcoms', title: 'WADComs', type: 'wadcoms', description: 'Windows Attack and Defense Commands' },
    { id: 'reverse-shell', title: 'Reverse Shell', type: 'reverse-shell', description: 'Reverse shell payloads and techniques' },
    { id: 'osint', title: 'OSINT Toolkit', type: 'osint', description: 'Open Source Intelligence tools' },
    { id: 'hunting-toolkit', title: 'Hunting Toolkit', type: 'hunting-toolkit', description: 'Threat hunting commands and techniques' }
  ]);
  const [isSaving, setIsSaving] = useState(false);
  
  // Search functionality states
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<{
    straightUp: Array<{
      methodologyId: string;
      subMethodologyId?: string;
      title: string;
      description?: string;
      type: 'straight-up';
    }>;
    guided: Array<{
      id: string;
      name: string;
      description: string;
      type: 'guided';
    }>;
  }>({ straightUp: [], guided: [] });
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Contribution review state
  const [pendingContributions, setPendingContributions] = useState<Contribution[]>([]);
  const [reviewLoading, setReviewLoading] = useState(true);
  const [reviewError, setReviewError] = useState("");
  const [manualContributor, setManualContributor] = useState({
    name: '', linkedin: '', twitter: '', reddit: '', contributionType: ''
  });
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [manualSuccess, setManualSuccess] = useState(false);
  const [manualError, setManualError] = useState("");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user || user.email !== ADMIN_EMAIL) {
        navigate('/aAyuiOcvMq');
      }
      setLoading(false);
    });

    // Fetch statistics
    const fetchStats = async () => {
      setUserCount(await methodologyService.getTotalUserCount());
      setActiveSessionCount(await methodologyService.getActiveSessionCount());
      
      // Fetch feedback statistics
      try {
        const feedbacks = await neuralNetworkService.getAllPathwayFeedback();
        setFeedbackCount(feedbacks.length);
        
        if (feedbacks.length > 0) {
          const totalRating = feedbacks.reduce((sum, fb) => sum + fb.rating, 0);
          setAverageRating(parseFloat((totalRating / feedbacks.length).toFixed(1)));
          
          const helpful = feedbacks.filter(fb => fb.wasHelpful).length;
          setHelpfulPercentage(parseFloat(((helpful / feedbacks.length) * 100).toFixed(1)));
        }
      } catch (error) {
        console.error('Error fetching feedback stats:', error);
      }
    };
    fetchStats();

    return () => unsubscribe();
  }, [navigate]);
  
  // Load all methodologies for the select dropdown
  useEffect(() => {
    const loadMethodologyOptions = async () => {
      try {
        // Load straight-up methodologies
        const straightUpMethodologies = await methodologyService.getMethodologies();
        const straightUpOptions = straightUpMethodologies.map(m => ({
          id: m.id,
          title: m.title,
          type: m.id === 'pathway' ? 'pathway' as const : 'straight-up' as const,
          description: m.description
        }));
        
        // Load guided methodologies
        const guidedMethodologies = await methodologyService.getGuidedMethodologies();
        const guidedOptions = guidedMethodologies
          .filter(m => m.status === 'Active')
          .map(m => ({
            id: m.id,
            title: `Guided: ${m.name}`,
            type: 'guided' as const,
            description: m.description
          }));
        
        // Combine all options
        setMethodologyOptions([...straightUpOptions, ...guidedOptions]);
      } catch (error) {
        console.error("Error loading methodologies:", error);
        toast({
          title: "Error",
          description: "Failed to load methodologies. Please refresh the page.",
          variant: "destructive",
        });
      }
    };
    
    loadMethodologyOptions();
  }, []);
  
  // Update local state when settings change
  useEffect(() => {
    if (settings.featuredMethodology !== undefined) {
      setFeaturedMethodology(settings.featuredMethodology);
    }
    if (settings.featuredToolkit !== undefined) {
      setFeaturedToolkit(settings.featuredToolkit);
    }
  }, [settings.featuredMethodology, settings.featuredToolkit]);
  
  // Handle featuring a methodology
  const handleFeatureMethodology = async (methodologyId: string) => {
    setIsSaving(true);
    try {
      if (methodologyId === "none") {
        // Clear featured methodology
        const updatedSettings = {
          ...settings,
          featuredMethodology: undefined
        };
        await methodologyService.updateSystemSettings(updatedSettings);
        setFeaturedMethodology(undefined);
      } else {
        const selected = methodologyOptions.find(m => m.id === methodologyId);
        if (selected) {
          const newFeaturedMethodology = {
            id: selected.id,
            title: selected.title,
            type: selected.type,
            description: selected.description
          };
          const updatedSettings = {
            ...settings,
            featuredMethodology: newFeaturedMethodology
          };
          await methodologyService.updateSystemSettings(updatedSettings);
          setFeaturedMethodology(newFeaturedMethodology);
        }
      }
      
      // Refresh the settings in the context
      await refreshSettings();
      
      toast({
        title: "Featured methodology updated",
        description: "Your changes have been saved successfully.",
      });
    } catch (error) {
      console.error("Error updating featured methodology:", error);
      toast({
        title: "Error",
        description: "Failed to update featured methodology. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle featuring a toolkit
  const handleFeatureToolkit = async (toolkitId: string) => {
    setIsSaving(true);
    try {
      if (toolkitId === "none") {
        // Clear featured toolkit
        const updatedSettings = {
          ...settings,
          featuredToolkit: undefined
        };
        await methodologyService.updateSystemSettings(updatedSettings);
        setFeaturedToolkit(undefined);
      } else {
        const selected = toolkitOptions.find(t => t.id === toolkitId);
        if (selected) {
          const newFeaturedToolkit = {
            id: selected.id,
            title: selected.title,
            type: selected.type,
            description: selected.description
          };
          const updatedSettings = {
            ...settings,
            featuredToolkit: newFeaturedToolkit
          };
          await methodologyService.updateSystemSettings(updatedSettings);
          setFeaturedToolkit(newFeaturedToolkit);
        }
      }
      
      // Refresh the settings in the context
      await refreshSettings();
      
      toast({
        title: "Featured toolkit updated",
        description: "Your changes have been saved successfully.",
      });
    } catch (error) {
      console.error("Error updating featured toolkit:", error);
      toast({
        title: "Error",
        description: "Failed to update featured toolkit. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      navigate('/aAyuiOcvMq');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  
  // Handle search - placeholder for future implementation
  const handleSearch = async (e?: React.FormEvent) => {
    e?.preventDefault();
    
    if (!searchQuery.trim()) {
      setSearchResults({ straightUp: [], guided: [] });
      setShowSearchResults(false);
      return;
    }
    
    setIsSearching(true);
    try {
      // TODO: Implement search functionality
      toast({
        title: "Search Not Available",
        description: "Search functionality is not yet implemented.",
      });
      setSearchResults({ straightUp: [], guided: [] });
    } catch (error) {
      console.error('Error searching methodologies:', error);
      toast({
        title: "Search Error",
        description: "Failed to search methodologies. Please try again.",
        variant: "destructive",
      });
      setSearchResults({ straightUp: [], guided: [] });
    } finally {
      setIsSearching(false);
    }
  };
  
  // Auto-generate keywords for a methodology - placeholder for future implementation
  const handleGenerateKeywords = async (methodologyId: string, type: 'straight-up' | 'guided', subMethodologyId?: string) => {
    setIsSaving(true);
    try {
      // TODO: Implement keyword generation functionality
      toast({
        title: "Keyword Generation Not Available",
        description: "Keyword generation functionality is not yet implemented.",
      });
    } catch (error) {
      console.error('Error generating keywords:', error);
      toast({
        title: "Error",
        description: "Failed to generate keywords. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  // Navigate to methodology edit page
  const handleEditMethodology = (methodologyId: string, type: 'straight-up' | 'guided', subMethodologyId?: string) => {
    if (type === 'straight-up') {
      if (subMethodologyId) {
        navigate(`/aAyuiOcvMq/methodologies/${methodologyId}/sub/${subMethodologyId}`);
      } else {
        navigate(`/aAyuiOcvMq/methodologies/${methodologyId}`);
      }
    } else if (type === 'guided') {
      navigate(`/aAyuiOcvMq/guided-methodologies/${methodologyId}`);
    }
  };

  useEffect(() => {
    contributionService.getPendingContributions().then(setPendingContributions).catch(() => setReviewError("Failed to load contributions")).finally(() => setReviewLoading(false));
  }, []);

  const handleAccept = async (id: string) => {
    await contributionService.acceptContribution(id, ADMIN_EMAIL);
    setPendingContributions(pendingContributions.filter(c => c.id !== id));
  };
  const handleReject = async (id: string) => {
    await contributionService.rejectContribution(id, ADMIN_EMAIL);
    setPendingContributions(pendingContributions.filter(c => c.id !== id));
  };
  const handleManualChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setManualContributor({ ...manualContributor, [e.target.name]: e.target.value });
  };
  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setManualSubmitting(true);
    setManualError("");
    setManualSuccess(false);
    try {
      await contributionService.addContributor(manualContributor as any);
      setManualSuccess(true);
      setManualContributor({ name: '', linkedin: '', twitter: '', reddit: '', contributionType: '' });
    } catch {
      setManualError("Failed to add contributor");
    } finally {
      setManualSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-black">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4">
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA Admin</span>
            </div>
            <div 
              onClick={handleSignOut}
              className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"
            >
              <img className="w-6 h-6" alt="Sign Out" src="/user-icon.svg" />
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 pt-[160px]">
        <div className="bg-white rounded-[20px] p-8 shadow-lg">
          <div className="mb-8">
            <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
              Admin Dashboard
            </h1>
            <p className="font-['Space_Grotesk'] text-sm text-black opacity-80">
              Manage your application
            </p>
          </div>

          {/* Search Section */}
          <div className="mb-6">
            <Card>
              <CardHeader>
                <CardTitle>Search Methodologies</CardTitle>
                <CardDescription>
                  Search for methodologies by keywords, titles, or content
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Input
                    placeholder="Search methodologies..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSearch(e);
                      }
                    }}
                    className="flex-1"
                  />
                  <Button 
                    onClick={(e) => handleSearch(e)}
                    disabled={isSearching}
                    className="bg-[#daed6e] hover:bg-[#c5d85c] text-black"
                  >
                    {isSearching ? "Searching..." : "Search"}
                  </Button>
                </div>
                
                {showSearchResults && (
                  <div className="mt-4">
                    <h3 className="text-lg font-medium mb-2">Search Results</h3>
                    
                    {searchResults.straightUp.length === 0 && searchResults.guided.length === 0 ? (
                      <p className="text-gray-500">No results found. Try different keywords.</p>
                    ) : (
                      <div className="space-y-4">
                        {searchResults.straightUp.length > 0 && (
                          <div>
                            <h4 className="font-medium mb-2">Straight-up Methodologies</h4>
                            <div className="space-y-2">
                              {searchResults.straightUp.map((result, index) => (
                                <div key={`straight-${index}`} className="p-3 border rounded-md">
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <h5 className="font-medium">{result.title}</h5>
                                      {result.description && (
                                        <p className="text-sm text-gray-600">{result.description}</p>
                                      )}
                                      <div className="mt-1">
                                        <Badge variant="outline" className="mr-1">
                                          {result.subMethodologyId ? 'Sub-methodology' : 'Main methodology'}
                                        </Badge>
                                      </div>
                                    </div>
                                    <div className="flex gap-2">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleEditMethodology(
                                          result.methodologyId,
                                          'straight-up',
                                          result.subMethodologyId
                                        )}
                                      >
                                        Edit
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleGenerateKeywords(
                                          result.methodologyId,
                                          'straight-up',
                                          result.subMethodologyId
                                        )}
                                        disabled={isSaving}
                                      >
                                        Generate Keywords
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {searchResults.guided.length > 0 && (
                          <div>
                            <h4 className="font-medium mb-2">Guided Methodologies</h4>
                            <div className="space-y-2">
                              {searchResults.guided.map((result, index) => (
                                <div key={`guided-${index}`} className="p-3 border rounded-md">
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <h5 className="font-medium">{result.name}</h5>
                                      {result.description && (
                                        <p className="text-sm text-gray-600">{result.description}</p>
                                      )}
                                      <div className="mt-1">
                                        <Badge variant="outline">Guided methodology</Badge>
                                      </div>
                                    </div>
                                    <div className="flex gap-2">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleEditMethodology(
                                          result.id,
                                          'guided'
                                        )}
                                      >
                                        Edit
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleGenerateKeywords(
                                          result.id,
                                          'guided'
                                        )}
                                        disabled={isSaving}
                                      >
                                        Generate Keywords
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Statistics Card */}
            <div className="bg-white p-6 rounded-[20px] border border-[#E7E7E7] shadow-sm">
              <h3 className="font-['Space_Grotesk'] font-bold text-lg text-black mb-4">
                Statistics
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-black opacity-80">Total Users</span>
                  <span className="font-bold">{userCount}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-black opacity-80">Active Sessions</span>
                  <span className="font-bold">{activeSessionCount}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-black opacity-80">Pathway Feedback</span>
                  <span className="font-bold">{feedbackCount}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-black opacity-80">Avg Rating</span>
                  <span className="font-bold">{averageRating}/5</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-black opacity-80">Helpful Rate</span>
                  <span className="font-bold">{helpfulPercentage}%</span>
                </div>
              </div>
            </div>

            {/* Quick Actions Card */}
            <div className="bg-white p-6 rounded-[20px] border border-[#E7E7E7] shadow-sm">
              <h3 className="font-['Space_Grotesk'] font-bold text-lg text-black mb-4">
                Quick Actions
              </h3>
              <div className="space-y-2">
                <Button 
                  className="w-full h-10 bg-[#daed6e] rounded-lg hover:bg-[#c5d85c] transition-colors"
                  onClick={() => navigate('/aAyuiOcvMq/all-methodologies')}
                >
                  <span className="font-['Space_Grotesk'] font-medium text-black text-sm">
                    All Methodologies
                  </span>
                </Button>
                <Button 
                  className="w-full h-10 bg-[#daed6e] rounded-lg hover:bg-[#c5d85c] transition-colors"
                  onClick={() => navigate('/aAyuiOcvMq/methodologies')}
                >
                  <span className="font-['Space_Grotesk'] font-medium text-black text-sm">
                    Straight-up Methodologies
                  </span>
                </Button>
                <Button 
                  className="w-full h-10 bg-[#daed6e] rounded-lg hover:bg-[#c5d85c] transition-colors"
                  onClick={() => navigate('/aAyuiOcvMq/neural-network')}
                >
                  <span className="font-['Space_Grotesk'] font-medium text-black text-sm">
                    Pathway Methodology
                  </span>
                </Button>
                <Button 
                  className="w-full h-10 bg-[#daed6e] rounded-lg hover:bg-[#c5d85c] transition-colors"
                  onClick={() => navigate('/aAyuiOcvMq/neural-feedback')}
                >
                  <span className="font-['Space_Grotesk'] font-medium text-black text-sm">
                    Pathway Feedback
                  </span>
                </Button>
                <Button 
                  className="w-full h-10 bg-[#daed6e] rounded-lg hover:bg-[#c5d85c] transition-colors"
                  onClick={() => navigate('/aAyuiOcvMq/guided-methodologies')}
                >
                  <span className="font-['Space_Grotesk'] font-medium text-black text-sm">
                    Guided Methodologies
                  </span>
                </Button>
                <Button 
                  className="w-full h-10 bg-[#daed6e] rounded-lg hover:bg-[#c5d85c] transition-colors"
                  onClick={() => navigate('/aAyuiOcvMq/hacker-checklists')}
                >
                  <span className="font-['Space_Grotesk'] font-medium text-black text-sm">
                    Hacker Checklists
                  </span>
                </Button>
                <Button 
                  className="w-full h-10 bg-[#daed6e] rounded-lg hover:bg-[#c5d85c] transition-colors"
                  onClick={() => navigate('/aAyuiOcvMq/notes')}
                >
                  <span className="font-['Space_Grotesk'] font-medium text-black text-sm">
                    Admin Notes
                  </span>
                </Button>
                <Button 
                  className="w-full h-10 bg-[#daed6e] rounded-lg hover:bg-[#c5d85c] transition-colors"
                  onClick={() => navigate('/aAyuiOcvMq/users')}
                >
                  <span className="font-['Space_Grotesk'] font-medium text-black text-sm">
                    Manage Users
                  </span>
                </Button>
                <Button 
                  className="w-full h-10 bg-[#daed6e] rounded-lg hover:bg-[#c5d85c] transition-colors"
                  onClick={() => navigate('/aAyuiOcvMq/settings')}
                >
                  <span className="font-['Space_Grotesk'] font-medium text-black text-sm">
                    System Settings
                  </span>
                </Button>
                <Button 
                  className="w-full h-10 bg-[#daed6e] rounded-lg hover:bg-[#c5d85c] transition-colors"
                  onClick={() => navigate('/aAyuiOcvMq/toolkit')}
                >
                  <span className="font-['Space_Grotesk'] font-medium text-black text-sm">
                    Toolkits
                  </span>
                </Button>
                <Button className="bg-[#daed6e] hover:bg-[#c5d85c] text-black w-full mb-2" onClick={() => navigate('/aAyuiOcvMq/contributions')}>
                  Review Contributions & Quick Add
                </Button>
                <Button 
                  className="w-full h-10 bg-[#daed6e] rounded-lg hover:bg-[#c5d85c] transition-colors"
                  onClick={() => navigate('/aAyuiOcvMq/two-factor-auth')}
                >
                  <span className="font-['Space_Grotesk'] font-medium text-black text-sm">
                    Manage 2FA
                  </span>
                </Button>
              </div>
            </div>
          </div>

          {/* Featured Links */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div 
              className="bg-white p-6 rounded-[20px] border border-[#E7E7E7] shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate('/aAyuiOcvMq/all-methodologies')}
            >
              <h3 className="font-['Space_Grotesk'] font-bold text-lg text-black mb-2">
                All Methodologies
              </h3>
              <p className="text-sm text-black opacity-80">
                View and manage all methodologies in one place
              </p>
            </div>
            
            <div 
              className="bg-white p-6 rounded-[20px] border border-[#E7E7E7] shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate('/aAyuiOcvMq/methodologies')}
            >
              <h3 className="font-['Space_Grotesk'] font-bold text-lg text-black mb-2">
                Straight-up Methodologies
              </h3>
              <p className="text-sm text-black opacity-80">
                Manage checklist-style methodologies
              </p>
            </div>
            
            <div 
              className="bg-white p-6 rounded-[20px] border border-[#E7E7E7] shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate('/aAyuiOcvMq/guided-methodologies')}
            >
              <h3 className="font-['Space_Grotesk'] font-bold text-lg text-black mb-2">
                Guided Methodologies
              </h3>
              <p className="text-sm text-black opacity-80">
                Manage interactive step-by-step guided methodologies
              </p>
            </div>
            <div 
              className="bg-white p-6 rounded-[20px] border border-[#E7E7E7] shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate('/aAyuiOcvMq/hacker-checklists')}
            >
              <h3 className="font-['Space_Grotesk'] font-bold text-lg text-black mb-2">
                Hacker Checklists
              </h3>
              <p className="text-sm text-black opacity-80">
                Publish playbooks and reusable checklists for hunters
              </p>
            </div>
            
            <div 
              className="bg-white p-6 rounded-[20px] border border-[#E7E7E7] shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate('/aAyuiOcvMq/neural-network')}
            >
              <h3 className="font-['Space_Grotesk'] font-bold text-lg text-black mb-2">
                Pathway Methodology
              </h3>
              <p className="text-sm text-black opacity-80">
                Manage AI-powered pathway generation with neural network learning
              </p>
            </div>
            
            <div 
              className="bg-white p-6 rounded-[20px] border border-[#E7E7E7] shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate('/aAyuiOcvMq/users')}
            >
              <h3 className="font-['Space_Grotesk'] font-bold text-lg text-black mb-2">
                User Management
              </h3>
              <p className="text-sm text-black opacity-80">
                Manage user accounts and permissions
              </p>
            </div>

            <div 
              className="bg-white p-6 rounded-[20px] border border-[#E7E7E7] shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate('/aAyuiOcvMq/notes')}
            >
              <h3 className="font-['Space_Grotesk'] font-bold text-lg text-black mb-2">
                Admin Notes
              </h3>
              <p className="text-sm text-black opacity-80">
                Create and manage admin-only notes
              </p>
            </div>
            
            <div 
              className="bg-white p-6 rounded-[20px] border border-[#E7E7E7] shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate('/aAyuiOcvMq/neural-feedback')}
            >
              <h3 className="font-['Space_Grotesk'] font-bold text-lg text-black mb-2">
                Pathway Feedback
              </h3>
              <p className="text-sm text-black opacity-80">
                View Feedback users leave after their pathway methodology usage
              </p>
            </div>
          </div>
          
          {/* Featured Methodology Section */}
          <div className="mt-4 bg-white p-6 rounded-[20px] border border-[#E7E7E7] shadow-sm">
            <h3 className="font-['Space_Grotesk'] font-bold text-lg text-black mb-4">
              Featured Methodology
            </h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="featured-methodology" className="font-medium">Select Methodology to Feature</Label>
                <p className="text-sm text-black opacity-80 mb-2">
                  This methodology will be highlighted on the user dashboard
                </p>
                
                <Select 
                  value={featuredMethodology?.id || "none"} 
                  onValueChange={handleFeatureMethodology}
                  disabled={isSaving}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a methodology to feature" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem key="none" value="none">None</SelectItem>
                    {methodologyOptions.map(option => (
                      <SelectItem key={option.id} value={option.id}>
                        {option.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {featuredMethodology && (
                <div className="p-4 border rounded-lg bg-[#f9f9f9]">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-medium">Currently Featured:</h3>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleFeatureMethodology("none")}
                      disabled={isSaving}
                      className="text-xs h-8"
                    >
                      {isSaving ? "Saving..." : "Remove"}
                    </Button>
                  </div>
                  <p className="font-medium">{featuredMethodology.title}</p>
                  {featuredMethodology.description && (
                    <p className="text-sm text-gray-600 mt-1">{featuredMethodology.description}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    Type: {featuredMethodology.type === 'straight-up' 
                      ? 'Straight-up Methodology' 
                      : featuredMethodology.type === 'guided'
                        ? 'Guided Methodology'
                        : 'Pathway Methodology'
                    }
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Featured Toolkit Section */}
          <div className="mt-4 bg-white p-6 rounded-[20px] border border-[#E7E7E7] shadow-sm">
            <h3 className="font-['Space_Grotesk'] font-bold text-lg text-black mb-4">
              Featured Toolkit
            </h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="featured-toolkit" className="font-medium">Select Toolkit to Feature</Label>
                <p className="text-sm text-black opacity-80 mb-2">
                  This toolkit will be highlighted on the user dashboard
                </p>
                
                <Select 
                  value={featuredToolkit?.id || "none"} 
                  onValueChange={handleFeatureToolkit}
                  disabled={isSaving}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a toolkit to feature" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem key="none" value="none">None</SelectItem>
                    {toolkitOptions.map(option => (
                      <SelectItem key={option.id} value={option.id}>
                        {option.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {featuredToolkit && (
                <div className="p-4 border rounded-lg bg-[#f9f9f9]">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-medium">Currently Featured:</h3>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleFeatureToolkit("none")}
                      disabled={isSaving}
                      className="text-xs h-8"
                    >
                      {isSaving ? "Saving..." : "Remove"}
                    </Button>
                  </div>
                  <p className="font-medium">{featuredToolkit.title}</p>
                  {featuredToolkit.description && (
                    <p className="text-sm text-gray-600 mt-1">{featuredToolkit.description}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    Type: {featuredToolkit.type === 'lolbas' 
                      ? 'LOLBAS' 
                      : featuredToolkit.type === 'gtfobins'
                        ? 'GTFOBins'
                        : featuredToolkit.type === 'wadcoms'
                          ? 'WADComs'
                          : featuredToolkit.type === 'reverse-shell'
                            ? 'Reverse Shell'
                            : featuredToolkit.type === 'osint'
                              ? 'OSINT Toolkit'
                              : 'Hunting Toolkit'
                    }
                  </p>
                </div>
              )}
            </div>
          </div>
          
          {/* System Settings Link */}
          <div className="mt-4">
            <div 
              className="bg-white p-6 rounded-[20px] border border-[#E7E7E7] shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate('/aAyuiOcvMq/settings')}
            >
              <h3 className="font-['Space_Grotesk'] font-bold text-lg text-black mb-2">
                System Settings
              </h3>
              <p className="text-sm text-black opacity-80">
                Configure global system settings and feature availability
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};